import { clubAt, playerNameAt } from './clubs.mock';
import type { ContestTeam, RosterPlayer, TeamPlayer } from '@/types';

/** Shaped exactly like the API, so screens behave the same either way. */

const POSITIONS: { position: TeamPlayer['position']; positionId: number; count: number }[] = [
  { position: 'GK', positionId: 24, count: 1 },
  { position: 'DEF', positionId: 25, count: 4 },
  { position: 'MID', positionId: 26, count: 3 },
  { position: 'FWD', positionId: 27, count: 3 },
];

/** One of each position on the bench, so every line has cover to swap in. */
const BENCH: typeof POSITIONS = POSITIONS.map((spec) => ({ ...spec, count: 1 }));

const players: TeamPlayer[] = [];
const benchPlayers: TeamPlayer[] = [];
let id = 900001;

for (const [target, specs] of [
  [players, POSITIONS],
  [benchPlayers, BENCH],
] as const) {
  for (const { position, positionId, count } of specs) {
    for (let n = 0; n < count; n += 1) {
      const index = players.length + benchPlayers.length;
      const club = clubAt(index);

      target.push({
        playerId: id,
        name: playerNameAt(index),
        displayName: playerNameAt(index),
        jerseyNumber: index + 1,
        positionId,
        position,
        teamId: club.teamId,
        teamName: club.name,
        teamShortCode: club.shortCode,
        isCaptain: id === 900001,
        isViceCaptain: id === 900002,
      });
      id += 1;
    }
  }
}

const startingPlayerIds = players.map((p) => p.playerId);
const benchPlayerIds = benchPlayers.map((p) => p.playerId);

export const MOCK_CONTEST_TEAM: ContestTeam = {
  id: 'tm_weekly',
  contestId: 'ct_weekly',
  seriesId: 900001,
  teamName: 'Mumbai Strikers',
  captainPlayerId: 900001,
  vicePlayerId: 900002,
  points: 12,
  rank: 1,
  transfersUsed: 0,
  transfersRemaining: 50,
  maxTransfers: 50,
  transferMode: 'lineup_swap',
  squadComplete: true,
  startingXiSize: 11,
  benchSize: 4,
  squadSize: 15,
  canEdit: true,
  transfersBlocked: false,
  nextLockAt: new Date(Date.now() + 3 * 86_400_000).toISOString(),
  currentFixtureWindowIndex: 0,
  startingPlayerIds,
  benchPlayerIds,
  playerIds: [...startingPlayerIds, ...benchPlayerIds],
  players,
  benchPlayers,
};

const POSITION_NAMES: Record<TeamPlayer['position'], string> = {
  GK: 'Goalkeeper',
  DEF: 'Defender',
  MID: 'Midfielder',
  FWD: 'Attacker',
};

/**
 * The pool the team builder draws from.
 *
 * Wide enough to build a legal eleven several ways — the builder needs at
 * least one of every position available at once, and enough depth that
 * filtering by position still leaves something to choose from.
 */
export const MOCK_ROSTER: readonly RosterPlayer[] = Array.from({ length: 60 }, (_, i) => {
  const spec = POSITIONS[i % POSITIONS.length] as (typeof POSITIONS)[number];
  const club = clubAt(i);

  // Offset past the picked squad so the pool does not repeat its names back
  // at someone browsing for a pick.
  const name = playerNameAt(i + players.length + benchPlayers.length);

  return {
    playerId: 901000 + i,
    seriesId: 900001,
    teamId: club.teamId,
    name,
    displayName: name,
    position: spec.position,
    positionId: spec.positionId,
    positionName: POSITION_NAMES[spec.position],
    jerseyNumber: i + 1,
    teamName: club.name,
    teamShortCode: club.shortCode,
    // Descending, so the pool has an obvious order without looking random.
    points: 120 - i * 2,
    gameweekPoints: (i % 9) + 1,
    form: Number((5 - (i % 5) * 0.7).toFixed(1)),
    status: 'available',
  };
});
